import React, { Component } from "react";
import styled from "styled-components";
import {
    FlexContainer,
    BusinessPortalHeader, LottieLoadingIndicator, CardList,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalCaption,
    PortalFormActions,
    PortalFormSection,
    PortalFormSectionDescription,
    PortalFormSectionHeader,
    PortalFormSectionTitle,
    PortalMetricCard,
    PortalMetricLabel,
    PortalPaymentOptionGrid,
    PortalPaymentSummaryAmount,
    PortalPaymentSummaryCard,
    PortalPaymentSummaryLabel,
    PortalPaymentSummaryRow,
    PortalPanelHeader,
    PortalSectionTitle,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";
import "@trendmicro/react-radio/dist/react-radio.css";
import { ClientLicenseContext as SubscriptionContext } from "../ClientLicenseContext";
import { FormStep, PurchaseFlowBody, PurchaseFlowPage, PurchaseFlowSurface } from "./PurchaseLicense";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { formatCurrency } from "src/utils/currency";

import { PaymentSelectionButton, PaymentSelectionIcon } from "src/scenes/Subscription/forms/Review";

const Screens = {
    OVERVIEW: "overview",
    PAYMENT: "payment"
};

export const Methods = {
    "EXISTING": "Existing Card",
    "NEW": "New Card"
};

const PurchaseOverviewGrid = styled.div`
    display: contents;
`;

const PaymentGrid = styled.div`
    display: contents;
`;

const PaymentMethodSection = styled(PortalFormSection)`
    min-width: 0;
`;

const PanelStack = styled(PortalFormSection)`
    width: 100%;
    display: grid;
    gap: 1rem;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const DetailCard = styled(PortalMetricCard)`
    min-height: auto;
    align-content: start;
    gap: 0.4rem;
`;

class PaymentConfirmation extends Component {
    state = {
        method: Methods.EXISTING,
        screen: Screens.OVERVIEW
    };

    componentDidMount() {
        const { getLicenseCostFromAmount } = this.context;
        getLicenseCostFromAmount();
    }

    static contextType = SubscriptionContext;

    updateMethod = (value) => {
        this.setState({
            method: value
        });
    };

    backStepToPagePurchaseLicense = () => {
        this.context.nextStep(FormStep.PAGE_PURCHASE_LICENSE);
    };

    nextClick = () => {
        this.context.nextStep(FormStep.PAGE_PURCHASE_SUCCESSFUL);
    };

    changeScreen = screen => this.setState({ screen });

    render() {
        //add method and updateValue when using radio buttons back
        const {
            companyName, abn,
            licenseCount, shortTermCost, gst, processingFee, total, currency,
            selectedCard,
            token, payUsingExistingCard, existingCardErrorString } = this.context;

        const { method, screen } = this.state;

        if (this.context.getLicenseCostFromAmountLoading) {
            return (
                <PurchaseFlowPage>
                    <BusinessPortalHeader title="Payment Confirmation" maxWidth={PAYMENT_FLOW_MAX_WIDTH} />
                    <LottieLoadingIndicator />
                </PurchaseFlowPage>
            );
        }

        return (
            <PurchaseFlowPage>
                <BusinessPortalHeader
                    title="Purchase Trial Licenses"
                    description={screen === Screens.OVERVIEW ?
                        "Review the selected business and license details before payment."
                        : "Select an existing payment method or add a new payment method for this purchase"
                    }
                    maxWidth={PAYMENT_FLOW_MAX_WIDTH}
                />

                { screen === Screens.OVERVIEW ?
                    <PurchaseFlowSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                        <PurchaseFlowBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                            <PurchaseOverviewGrid>
                                <Price
                                    companyName={companyName}
                                    licenseCount={licenseCount}
                                    shortTermCost={shortTermCost}
                                    total={total}
                                />
                                <OrderSummary
                                    licenseCount={licenseCount}
                                    shortTermCost={shortTermCost}
                                    gst={gst}
                                    processingFee={processingFee}
                                    currency={currency}
                                    total={total}
                                />
                            </PurchaseOverviewGrid>
                        </PurchaseFlowBody>
                        <PortalFormActions sticky={false}>
                            <StandardModalActionGroup $pushRight>
                                <StandardModalActionButton type="button" onClick={() => this.backStepToPagePurchaseLicense()}>
                                    Back
                                </StandardModalActionButton>
                                <StandardModalActionButton
                                    type="button"
                                    $variant="primary"
                                    minWidth="11.125rem"
                                    width="11.125rem"
                                    onClick={() => this.changeScreen(Screens.PAYMENT)}
                                >
                                    Proceed to Payment
                                </StandardModalActionButton>
                            </StandardModalActionGroup>
                        </PortalFormActions>
                    </PurchaseFlowSurface>
                    :
                    <PurchaseFlowSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                        <PurchaseFlowBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                            <PaymentGrid>
                                <OrderSummary
                                    licenseCount={licenseCount}
                                    shortTermCost={shortTermCost}
                                    gst={gst}
                                    processingFee={processingFee}
                                    currency={currency}
                                    total={total}
                                />
                                <PaymentMethodSection>
                                    <PortalFormSectionHeader withDivider>
                                        <PortalFormSectionTitle>Payment Method</PortalFormSectionTitle>
                                        <PortalFormSectionDescription>
                                            Select an existing payment method or add a new card.
                                        </PortalFormSectionDescription>
                                    </PortalFormSectionHeader>
                                    <PortalPaymentOptionGrid>
                                        <PaymentSelectionButton
                                            $active={method === Methods.EXISTING}
                                            onClick={() => this.updateMethod(Methods.EXISTING)}
                                        >

                                            <PaymentSelectionIcon
                                                $active={method === Methods.EXISTING}
                                            />

                                            Use an existing card
                                        </PaymentSelectionButton>
                                        <PaymentSelectionButton
                                            $active={method === Methods.NEW}
                                            onClick={() => this.updateMethod(Methods.NEW)}
                                        >
                                            <PaymentSelectionIcon
                                                $active={method === Methods.NEW}
                                            />
                                            Add a new card
                                        </PaymentSelectionButton>
                                    </PortalPaymentOptionGrid>
                                    { method === Methods.NEW ?
                                        <FlexContainer>
                                            <AirwallexForm
                                                token={token}
                                                productType={AirwallexProduct.CLIENT_LICENSES}
                                                companyName={companyName} abn={abn} numberOfUsers={licenseCount}
                                                backToPrevious={ this.backStepToPagePurchaseLicense }
                                                nextClick={ this.nextClick }
                                            />
                                        </FlexContainer>
                                        :
                                        <CardList
                                            token={token}
                                            selectedCard={selectedCard}
                                            loadingError={existingCardErrorString}
                                            onBackClicked={() => this.backStepToPagePurchaseLicense()}
                                            onPurchaseClicked={payUsingExistingCard}
                                            onDeleteClicked={null}
                                            hideBackButton
                                            minHeight="auto"
                                        />

                                    }
                                </PaymentMethodSection>
                            </PaymentGrid>
                        </PurchaseFlowBody>
                        <PortalFormActions sticky={false}>
                            <StandardModalActionGroup $pushRight>
                                <StandardModalActionButton type="button" onClick={() => this.changeScreen(Screens.OVERVIEW)}>
                                    Back
                                </StandardModalActionButton>
                            </StandardModalActionGroup>
                        </PortalFormActions>
                    </PurchaseFlowSurface>
                }
            </PurchaseFlowPage>
        );
    }
}

class Price extends Component {
    render() {
        const { companyName, licenseCount } = this.props;

        return (
            <PanelStack>
                <PortalPanelHeader withDivider>
                    <div>
                        <PortalSectionTitle>Purchase Overview</PortalSectionTitle>
                        <PortalCaption margin="0.25rem 0 0">
                            Review the selected business and trial license details before payment.
                        </PortalCaption>
                    </div>
                </PortalPanelHeader>

                <DetailGrid>
                    <DetailCard minHeight="auto">
                        <PortalMetricLabel>Business Name</PortalMetricLabel>
                        <PortalSectionTitle size="1rem">{companyName}</PortalSectionTitle>
                    </DetailCard>

                    <DetailCard minHeight="auto">
                        <PortalMetricLabel>Trial Licenses</PortalMetricLabel>
                        <PortalSectionTitle size="1rem">
                            {parseInt(licenseCount).toLocaleString("en-us")}
                        </PortalSectionTitle>
                    </DetailCard>
                </DetailGrid>

                <DetailCard minHeight="auto">
                    <PortalMetricLabel>License Destination</PortalMetricLabel>
                    <PortalSectionTitle size="1rem">Sales & Promotions</PortalSectionTitle>
                    <PortalCaption margin="0">
                        Purchased licenses will be added to your business Sales & Promotions feature after payment.
                    </PortalCaption>
                </DetailCard>
            </PanelStack>
        );
    }
}

const OrderSummary = (props) => {
    const { licenseCount, shortTermCost, gst, total, currency, processingFee } = props;

    return (
        <PanelStack>
            <PortalPanelHeader withDivider>
                <div>
                    <PortalSectionTitle>Order Summary</PortalSectionTitle>
                    <PortalCaption margin="0.25rem 0 0">
                        Confirm the license duration, quantity, and total before payment.
                    </PortalCaption>
                </div>
            </PortalPanelHeader>

            <DetailCard minHeight="auto">
                <PortalMetricLabel>Trial License Duration</PortalMetricLabel>
                <PortalSectionTitle size="1rem">12 Months</PortalSectionTitle>
            </DetailCard>

            <DetailCard minHeight="auto">
                <PortalMetricLabel>Number of Trial Licenses</PortalMetricLabel>
                <PortalSectionTitle size="1rem">{licenseCount}</PortalSectionTitle>
            </DetailCard>

            <DetailCard minHeight="auto">
                <PortalMetricLabel>Price Per Trial License</PortalMetricLabel>
                <PortalSectionTitle size="1rem">
                    {formatCurrency(shortTermCost, currency)}
                </PortalSectionTitle>
            </DetailCard>

            <PortalPaymentSummaryCard>
                <PortalPaymentSummaryRow>
                    <PortalPaymentSummaryLabel>Subtotal</PortalPaymentSummaryLabel>
                    <PortalPaymentSummaryAmount>
                        {formatCurrency(licenseCount * shortTermCost, currency)}
                    </PortalPaymentSummaryAmount>
                </PortalPaymentSummaryRow>
                { processingFee > 0 &&
                    <PortalPaymentSummaryRow>
                        <PortalPaymentSummaryLabel>Processing Fee</PortalPaymentSummaryLabel>
                        <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                            {formatCurrency(processingFee, currency)}
                        </PortalPaymentSummaryAmount>
                    </PortalPaymentSummaryRow>
                }
                { gst > 0 &&
                    <PortalPaymentSummaryRow>
                        <PortalPaymentSummaryLabel>GST</PortalPaymentSummaryLabel>
                        <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                            {formatCurrency(gst, currency)}
                        </PortalPaymentSummaryAmount>
                    </PortalPaymentSummaryRow>
                }
                <PortalPaymentSummaryRow>
                    <PortalPaymentSummaryLabel>Total</PortalPaymentSummaryLabel>
                    <PortalPaymentSummaryAmount size="1.25rem">
                        {formatCurrency(total, currency, true)}
                    </PortalPaymentSummaryAmount>
                </PortalPaymentSummaryRow>
            </PortalPaymentSummaryCard>
        </PanelStack>
    );
};

export default PaymentConfirmation;
